import { useState, useEffect } from 'react';
import { useAuth } from '../components/auth/AuthProvider';
import { supabase } from '../lib/supabase';

export interface UserProfile {
  id?: string;
  user_id?: string;
  first_name?: string;
  last_name?: string;
  phone?: string;
  address?: string;
  city?: string;
  state?: string;
  zip_code?: string;
  country?: string;
  created_at?: string;
  updated_at?: string;
}

export const useUserProfile = () => {
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { user } = useAuth();

  const fetchProfile = async () => {
    if (!user) {
      setLoading(false);
      return;
    }

    // Check if Supabase is properly configured
    if (!import.meta.env.VITE_SUPABASE_URL || !import.meta.env.VITE_SUPABASE_ANON_KEY) {
      console.error('Supabase environment variables not configured');
      setError('Database connection not configured. Please check environment variables.');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const { data, error: fetchError } = await supabase
        .from('user_profiles')
        .select('*')
        .eq('user_id', user.id)
        .maybeSingle();

      if (fetchError) {
        console.error('Supabase fetch error:', fetchError);
        throw fetchError;
      }

      setProfile(data);
    } catch (err) {
      console.error('Error fetching user profile:', err);
      
      // Handle different types of connection errors
      if (err instanceof TypeError && err.message.includes('Failed to fetch')) {
        setError('Unable to connect to database. Please ensure Supabase is properly configured.');
      } else if (err instanceof Error && err.message.includes('Invalid API key')) {
        setError('Database authentication failed. Please check your Supabase configuration.');
      } else if (err instanceof Error && err.message.includes('Network request failed')) {
        setError('Network connection failed. Please check your internet connection.');
      } else {
        setError('Database connection unavailable. Profile features are temporarily disabled.');
      }
      
      // Set a default empty profile to prevent UI crashes
      setProfile(null);
    } finally {
      setLoading(false);
    }
  };

  const saveProfile = async (profileData: Partial<UserProfile>) => {
    if (!user) {
      throw new Error('User not authenticated');
    }

    // Check if Supabase is configured before attempting to save
    if (!import.meta.env.VITE_SUPABASE_URL || !import.meta.env.VITE_SUPABASE_ANON_KEY) {
      const errorMessage = 'Database connection not configured. Cannot save profile.';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    }

    // Validate profile data before saving
    const validationErrors: string[] = [];
    
    if (profileData.first_name && profileData.first_name.trim().length < 2) {
      validationErrors.push('First name must be at least 2 characters long');
    }
    
    if (profileData.last_name && profileData.last_name.trim().length < 2) {
      validationErrors.push('Last name must be at least 2 characters long');
    }
    
    if (profileData.phone && profileData.phone.trim()) {
      const phoneRegex = /^[\+]?[1-9][\d]{0,15}$/;
      const cleanPhone = profileData.phone.replace(/[^\d+]/g, '');
      if (!phoneRegex.test(cleanPhone)) {
        validationErrors.push('Please enter a valid phone number');
      }
    }
    
    if (validationErrors.length > 0) {
      const errorMessage = validationErrors.join(', ');
      setError(errorMessage);
      return { success: false, error: errorMessage };
    }
    try {
      setError(null);

      const dataToSave = {
        user_id: user.id,
        first_name: profileData.first_name?.trim(),
        last_name: profileData.last_name?.trim(),
        phone: profileData.phone?.trim(),
        address: profileData.address?.trim(),
        city: profileData.city?.trim(),
        state: profileData.state?.trim(),
        zip_code: profileData.zip_code?.trim(),
        country: profileData.country?.trim(),
      };

      const { data, error: saveError } = await supabase
        .from('user_profiles')
        .upsert(dataToSave, {
          onConflict: 'user_id',
          ignoreDuplicates: false,
        })
        .select()
        .single();

      if (saveError) {
        throw saveError;
      }

      setProfile(data);
      return { success: true, data };
    } catch (err) {
      console.error('Error saving user profile:', err);
      
      let errorMessage: string;
      if (err instanceof TypeError && err.message.includes('Failed to fetch')) {
        errorMessage = 'Unable to connect to database. Please check your connection and try again.';
      } else if (err instanceof Error && err.message.includes('Invalid API key')) {
        errorMessage = 'Database authentication failed. Please check your configuration.';
      } else {
        errorMessage = err instanceof Error ? err.message : 'Failed to save profile';
      }
      
      setError(errorMessage);
      return { success: false, error: errorMessage };
    }
  };

  const updateProfile = async (updates: Partial<UserProfile>) => {
    if (!user || !profile) {
      throw new Error('User not authenticated or profile not loaded');
    }

    // Check if Supabase is configured
    if (!import.meta.env.VITE_SUPABASE_URL || !import.meta.env.VITE_SUPABASE_ANON_KEY) {
      const errorMessage = 'Database connection not configured. Cannot update profile.';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    }

    try {
      setError(null);

      const { data, error: updateError } = await supabase
        .from('user_profiles')
        .update(updates)
        .eq('user_id', user.id)
        .select()
        .single();

      if (updateError) {
        throw updateError;
      }

      setProfile(data);
      return { success: true, data };
    } catch (err) {
      console.error('Error updating user profile:', err);
      
      let errorMessage: string;
      if (err instanceof TypeError && err.message.includes('Failed to fetch')) {
        errorMessage = 'Unable to connect to database. Please check your connection and try again.';
      } else {
        errorMessage = err instanceof Error ? err.message : 'Failed to update profile';
      }
      
      setError(errorMessage);
      return { success: false, error: errorMessage };
    }
  };

  useEffect(() => {
    fetchProfile();
  }, [user]);

  return {
    profile,
    loading,
    error,
    saveProfile,
    updateProfile,
    refreshProfile: fetchProfile,
  };
};